// S2 · Step 1 — Vertical picker (`/onboarding/vertical`, Module 02 §4 S2, §3.2).
//
// The screen that turns a blank tenant into a factory that speaks its own
// industry: list the packs, preview one in full, install it, and watch the seed
// run. Everything goes through the generated api-client (doctrine 3):
//
//   GET  /v1/verticals                 → the pack cards
//   GET  /v1/verticals/{key}           → the preview drawer (pack-preview.tsx)
//   POST /v1/verticals/{key}/install   → 202, then
//   GET  /v1/verticals/installs/current→ polled every 2 s while `installing`
//   PUT  /v1/onboarding/steps/1/payload + POST …/complete (SaveContinueBar)
//
// Three things this screen is deliberately strict about:
//
//  1. HONESTY (§3.2). The install response separates seeded / snapshot / skipped
//     kinds; the breakdown renders all three, and a skipped kind reads "Pending
//     <module>" — never as installed. See install-breakdown.tsx.
//  2. PERMISSIONS (§7). Installing needs `onboarding.install_pack` (group_admin
//     only). Without it the screen is a full-fidelity read-only view: every pack
//     still previewable (that is `onboarding.view`), no install button offered.
//     The client mirror is a convenience — the server is the boundary, and it
//     fails closed while the role feed settles.
//  3. FROZEN (§3.1). Once the tenant is live every wizard mutation answers 409
//     ALREADY_LIVE, so the write controls disappear rather than 409-ing on click.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { StatusChip } from "../../../components/status-chip";
import { CardSkeleton, EmptyState, ErrorState, TableSkeleton } from "../../../components/states";
import { formatTimestamp } from "../../../lib/datetime";
import { UnauthorizedError } from "../../../lib/problem";
import { requestIdOf } from "../../../lib/request";
import { SaveContinueBar } from "../components/save-continue-bar";
import { StepContainer } from "../components/step-container";
import { WizardDialog } from "../components/wizard-dialog";
import { WizardProblemNotice } from "../components/wizard-notices";
import {
  useCurrentInstall,
  useInstallVertical,
  useOnboardingSession,
  useVerticals,
} from "../hooks/onboarding-queries";
import { useOnboardingPermissions } from "../hooks/permissions";
import { isCode } from "../lib/wizard-problems";
import { isWizardFrozen, stepStateOf } from "../lib/wizard-steps";
import { InstallBreakdown } from "./vertical/install-breakdown";
import {
  activePackKey,
  installError,
  installPhase,
  installTotals,
  isActivePack,
  kindRows,
  localeText,
  needsForce,
  topCounts,
  totalCount,
  type CurrentInstall,
  type PackSummary,
} from "./vertical/install-model";
import { PackPreviewDrawer } from "./vertical/pack-preview";
import "./vertical/vertical.css";

/** What the confirm panel is about to do. */
interface PendingInstall {
  key: string;
  name: string;
  force: boolean;
}

export function VerticalPickerScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useOnboardingPermissions();
  const session = useOnboardingSession();
  const packs = useVerticals();
  const current = useCurrentInstall({ pollWhileInstalling: true });
  const install = useInstallVertical();

  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingInstall | null>(null);
  const [previewed, setPreviewed] = useState<readonly string[]>([]);
  const hydrated = useRef(false);

  const step = stepStateOf(session.data?.steps ?? [], 1);
  const frozen = session.data !== undefined && isWizardFrozen(session.data.session);
  // §7: install/switch is group_admin-only. Fail-closed until the role feed
  // settles, and never offered once the wizard is frozen.
  const canInstall = perms.has("onboarding.install_pack") && !frozen;

  // Re-seed the "packs I looked at" scratch state from the server copy (§2.2
  // payload example `{"previewed_packs":[…]}`) exactly once per load, and again
  // whenever a 409 STALE_PAYLOAD hands us the server's version.
  useEffect(() => {
    if (hydrated.current || step === undefined) return;
    hydrated.current = true;
    setPreviewed(readPreviewed(step.payload));
  }, [step]);

  const unauthorized =
    packs.error instanceof UnauthorizedError || current.error instanceof UnauthorizedError;
  useEffect(() => {
    if (unauthorized) void navigate({ to: "/login" });
  }, [unauthorized, navigate]);

  const currentData: CurrentInstall | null = current.data ?? null;
  const phase = installPhase(currentData);
  const active = activePackKey(currentData);

  function openPreview(key: string): void {
    setPreviewKey(key);
    setPreviewed((list) => (list.includes(key) ? list : [...list, key]));
  }

  function startInstall(pack: PackSummary): void {
    setPending({
      key: pack.key,
      name: packName(pack, i18n.language, t("onboarding.vertical.card.unnamed")),
      force: needsForce(currentData, pack.key),
    });
  }

  function confirmInstall(): void {
    if (pending === null) return;
    const body = pending.force ? { key: pending.key, force: true } : { key: pending.key };
    install.mutate(body, {
      onSuccess: () => {
        setPending(null);
      },
    });
  }

  /** Retry a FAILED install. A failed row is not "active", so this never needs
   * force and never supersedes anyone's work — the one case that may go
   * straight to the API without re-confirming. */
  function reinstall(key: string): void {
    install.mutate({ key });
  }

  /**
   * Recovery from 409 PACK_ALREADY_INSTALLED: our `current` was stale, so what
   * the user asked for is actually a §3.2 pt-4 SWITCH. It re-opens the confirm
   * panel in force mode rather than firing the force POST, because switching
   * supersedes another admin's install and orphans its seeded objects — the
   * four consequences must be read before, not discovered after.
   */
  function escalateToSwitch(): void {
    install.reset();
    setPending((p) => (p === null ? null : { ...p, force: true }));
    void current.refetch();
  }

  const footer = (
    <SaveContinueBar
      stepNo={1}
      getPayload={() => ({
        previewed_packs: [...previewed],
        selected_pack_key: active ?? null,
      })}
      updatedAt={step?.updated_at}
      // The DERIVED scratch, not the raw payload (§3.8) — and derived the SAME
      // WAY `getPayload` derives it, member for member. That is the whole
      // contract of this prop: the baseline is what an UNTOUCHED editor would
      // send, so only a real edit can read "Unsaved changes".
      //
      // `selected_pack_key` was previously derived here from the saved payload
      // while `getPayload` derives it from the INSTALL read. They are different
      // sources, so any tenant whose pack was installed without step 1 later
      // saving that key — the seeded KazKabel design-partner tenant among them —
      // was permanently dirty: a bare load of /onboarding/vertical, zero input,
      // read "You have unsaved changes." and clicking anything in the left nav
      // raised "Leave without saving? … If you leave now they are lost." over
      // work nobody had done (surface review F1, a blocker).
      //
      // It is derived from `active` on BOTH sides now, which is correct rather
      // than merely quiet: the selected pack is not editor state at all — it is
      // a mirror of a server read, changed only by an install, which is itself
      // an immediately-applied server mutation. A member the user cannot edit
      // can never be unsaved work. `previewed_packs` IS editor state and still
      // drives the dirty flag on its own.
      loadedPayload={
        step === undefined
          ? undefined
          : {
              previewed_packs: readPreviewed(step.payload),
              selected_pack_key: active ?? null,
            }
      }
      onStale={(serverStep) => {
        setPreviewed(readPreviewed(serverStep.payload));
      }}
      continueTo="/onboarding/catalogue"
      frozen={frozen}
    >
      {!current.isPending && phase !== "installed" ? (
        // §3.1: every step's completion is gated on an installed pack, so say so
        // here rather than letting the server's 422 be the first anyone hears.
        <p className="wizard-save-bar-note">{t("onboarding.vertical.completeHint")}</p>
      ) : null}
    </SaveContinueBar>
  );

  return (
    <StepContainer
      stepNo={1}
      titleKey="onboarding.steps.vertical.title"
      blurbKey="onboarding.steps.vertical.blurb"
      footer={footer}
    >
      <div className="vp-screen">
        <p className="vp-intro">{t("onboarding.vertical.packsSubtitle")}</p>

        {frozen ? (
          <p className="vp-note">{t("onboarding.vertical.frozenNote")}</p>
        ) : perms.isResolved && !canInstall ? (
          <p className="vp-note">{t("onboarding.vertical.readOnlyNote")}</p>
        ) : null}

        <CurrentInstallPanel
          current={currentData}
          isPending={current.isPending}
          error={current.error}
          onRetryRead={() => void current.refetch()}
          onRetryInstall={(key) => {
            reinstall(key);
          }}
          canInstall={canInstall}
          locale={i18n.language}
          packs={packs.data?.items ?? []}
        />

        <InstallProblem
          error={install.error}
          onSwitch={escalateToSwitch}
          onReloadPacks={() => {
            install.reset();
            void packs.refetch();
            void current.refetch();
          }}
          onRetry={() => {
            if (pending !== null) confirmInstall();
          }}
        />

        {pending !== null ? (
          <ConfirmPanel
            pending={pending}
            currentName={
              active === undefined
                ? undefined
                : packName(
                    (packs.data?.items ?? []).find((p) => p.key === active),
                    i18n.language,
                    active,
                  )
            }
            busy={install.isPending}
            onConfirm={confirmInstall}
            onCancel={() => {
              setPending(null);
              install.reset();
            }}
          />
        ) : null}

        <section aria-labelledby="vp-packs-heading">
          <h2 id="vp-packs-heading">{t("onboarding.vertical.packsHeading")}</h2>

          {packs.isPending || unauthorized ? (
            <TableSkeleton rows={2} cols={3} label={t("onboarding.vertical.loading")} />
          ) : packs.error !== null ? (
            <ErrorState
              title={t("onboarding.vertical.errorTitle")}
              detail={t("onboarding.vertical.errorDetail")}
              retryLabel={t("onboarding.state.retry")}
              onRetry={() => void packs.refetch()}
              requestId={requestIdOf(packs.error)}
              requestIdLabel={t("onboarding.state.requestId")}
            />
          ) : packs.data.items.length === 0 ? (
            <EmptyState
              title={t("onboarding.vertical.emptyTitle")}
              body={t("onboarding.vertical.emptyBody")}
            />
          ) : (
            <ul className="vp-pack-grid">
              {packs.data.items.map((pack) => (
                <PackCard
                  key={pack.key}
                  pack={pack}
                  locale={i18n.language}
                  isActive={isActivePack(currentData, pack.key)}
                  phase={phase}
                  needsSwitch={needsForce(currentData, pack.key)}
                  canInstall={canInstall}
                  busy={install.isPending}
                  onPreview={() => {
                    openPreview(pack.key);
                  }}
                  onInstall={() => {
                    startInstall(pack);
                  }}
                />
              ))}
            </ul>
          )}
        </section>
      </div>

      {previewKey !== null ? (
        <PackPreviewDrawer
          packKey={previewKey}
          fallbackName={packName(
            (packs.data?.items ?? []).find((p) => p.key === previewKey),
            i18n.language,
            previewKey,
          )}
          onClose={() => {
            setPreviewKey(null);
          }}
        />
      ) : null}
    </StepContainer>
  );
}

// ---------------------------------------------------------------------------
// pieces
// ---------------------------------------------------------------------------

function PackCard({
  pack,
  locale,
  isActive,
  phase,
  needsSwitch,
  canInstall,
  busy,
  onPreview,
  onInstall,
}: {
  pack: PackSummary;
  locale: string;
  isActive: boolean;
  phase: ReturnType<typeof installPhase>;
  /** Another pack is active, so installing this one is a §3.2 pt-4 switch. */
  needsSwitch: boolean;
  canInstall: boolean;
  busy: boolean;
  onPreview: () => void;
  onInstall: () => void;
}) {
  const { t } = useTranslation();
  const name = packName(pack, locale, t("onboarding.vertical.card.unnamed"));
  const description = localeText(pack.description, locale);
  const installedHere = isActive && phase === "installed";
  const installingHere = isActive && phase === "installing";

  return (
    <li className={`ds-card vp-pack-card${isActive ? " is-active" : ""}`} data-pack={pack.key}>
      <div className="vp-pack-head">
        <div>
          <h3 className="vp-pack-title">{name}</h3>
          <p className="vp-pack-version">
            {t("onboarding.vertical.card.version", { version: pack.version })}
          </p>
        </div>
        {installedHere ? (
          <StatusChip variant="ok">{t("onboarding.vertical.card.installedBadge")}</StatusChip>
        ) : installingHere ? (
          <StatusChip variant="accent">{t("onboarding.vertical.card.installingBadge")}</StatusChip>
        ) : null}
      </div>

      <p className="vp-pack-description">
        {description ?? t("onboarding.vertical.card.noDescription")}
      </p>

      <p className="vp-pack-version">
        {t("onboarding.vertical.card.objects", { count: totalCount(pack.counts_by_kind) })}
      </p>
      <ul className="vp-pack-counts">
        {topCounts(pack.counts_by_kind, 5).map((entry) => (
          <li className="vp-count-chip" key={entry.kind}>
            {t("onboarding.vertical.card.countLine", {
              count: entry.count,
              kind: kindLabel(t, entry.kind),
            })}
          </li>
        ))}
      </ul>

      <div className="vp-pack-actions">
        <Button onClick={onPreview}>{t("onboarding.vertical.card.previewCta")}</Button>
        {canInstall && !installedHere && !installingHere ? (
          <Button variant="primary" disabled={busy} onClick={onInstall}>
            {needsSwitch
              ? t("onboarding.vertical.card.switchCta")
              : t("onboarding.vertical.card.installCta")}
          </Button>
        ) : null}
      </div>
    </li>
  );
}

function CurrentInstallPanel({
  current,
  isPending,
  error,
  onRetryRead,
  onRetryInstall,
  canInstall,
  locale,
  packs,
}: {
  current: CurrentInstall | null;
  isPending: boolean;
  error: unknown;
  onRetryRead: () => void;
  onRetryInstall: (key: string) => void;
  canInstall: boolean;
  locale: string;
  packs: readonly PackSummary[];
}) {
  const { t } = useTranslation();

  if (isPending) {
    return <CardSkeleton lines={2} label={t("onboarding.vertical.current.loading")} />;
  }
  if (error !== null && error !== undefined) {
    return (
      <ErrorState
        title={t("onboarding.vertical.current.errorTitle")}
        detail={t("onboarding.vertical.current.errorDetail")}
        retryLabel={t("onboarding.state.retry")}
        onRetry={onRetryRead}
        requestId={requestIdOf(error)}
        requestIdLabel={t("onboarding.state.requestId")}
      />
    );
  }
  // No install yet is a legitimate state, not an error: the pack grid below is
  // the whole screen (§4 S2 has no separate empty state for it).
  if (current === null) return null;

  const row = current.install;
  const name = packName(
    packs.find((p) => p.key === row.pack_key),
    locale,
    row.pack_key,
  );
  const rows = kindRows(current);
  const totals = installTotals(rows);
  const failure = installError(row);

  if (row.status === "installing") {
    return (
      <section className="ds-card vp-banner" data-testid="install-installing" aria-live="polite">
        <div className="vp-banner-head">
          <h2>{t("onboarding.vertical.current.installingTitle", { name })}</h2>
          <StatusChip variant="accent">{t("onboarding.vertical.card.installingBadge")}</StatusChip>
        </div>
        <p>{t("onboarding.vertical.current.installingBody")}</p>
        <div className="vp-progress">
          <span
            className="vp-progress-track"
            role="progressbar"
            aria-valuenow={totals.percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t("onboarding.vertical.current.progress", {
              done: totals.materialized,
              total: totals.expected,
            })}
          >
            <span className="vp-progress-value" style={{ width: `${totals.percent}%` }} />
          </span>
          <span className="vp-progress-text">
            {t("onboarding.vertical.current.progress", {
              done: totals.materialized,
              total: totals.expected,
            })}
          </span>
        </div>
        <InstallBreakdown rows={rows} />
      </section>
    );
  }

  if (row.status === "failed") {
    return (
      <section className="ds-card vp-banner is-failed" data-testid="install-failed" role="alert">
        <div className="vp-banner-head">
          <h2>{t("onboarding.vertical.current.failedTitle", { name })}</h2>
          <StatusChip variant="bad">{t("onboarding.vertical.card.failedBadge")}</StatusChip>
        </div>
        <p>{t("onboarding.vertical.current.failedBody")}</p>
        {/* The server's raw code + message are English identifiers, so they are
            never the EXPLANATION — a translated sentence is, and the raw text
            sits behind a "technical detail" disclosure for a support ticket
            (the i18n rule). */}
        {failure?.code !== undefined || failure?.detail !== undefined ? (
          <details className="vp-note vp-technical">
            <summary>{t("onboarding.vertical.current.technicalDetail")}</summary>
            {failure.code !== undefined ? (
              <p>
                <code>{failure.code}</code>
              </p>
            ) : null}
            {failure.detail !== undefined ? (
              <p>
                <code>{failure.detail}</code>
              </p>
            ) : null}
          </details>
        ) : null}
        {canInstall ? (
          <p>
            <Button
              variant="primary"
              onClick={() => {
                onRetryInstall(row.pack_key);
              }}
            >
              {t("onboarding.vertical.current.retryCta")}
            </Button>
          </p>
        ) : null}
        <InstallBreakdown rows={rows} />
      </section>
    );
  }

  if (row.status === "superseded") {
    return (
      <section className="ds-card vp-banner" data-testid="install-superseded">
        <h2>{t("onboarding.vertical.current.supersededTitle", { name })}</h2>
        <p>{t("onboarding.vertical.current.supersededBody")}</p>
      </section>
    );
  }

  return (
    <section className="ds-card vp-banner" data-testid="install-installed">
      <div className="vp-banner-head">
        <h2>{t("onboarding.vertical.current.installedTitle", { name })}</h2>
        <StatusChip variant="ok">{t("onboarding.vertical.card.installedBadge")}</StatusChip>
      </div>
      <p>
        {t("onboarding.vertical.current.installedBody", {
          version: row.pack_version,
          date:
            row.finished_at === null
              ? t("onboarding.vertical.current.finishedUnknown")
              : formatTimestamp(row.finished_at, locale),
        })}
      </p>
      <InstallBreakdown rows={rows} />
    </section>
  );
}

function ConfirmPanel({
  pending,
  currentName,
  busy,
  onConfirm,
  onCancel,
}: {
  pending: PendingInstall;
  currentName: string | undefined;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  // A modal, not an in-flow panel: this used to render ABOVE the pack grid, so
  // pressing Install on a card lower down looked like nothing happened — the
  // confirmation, consequences and all, opened off-screen.
  return (
    <WizardDialog
      testId="install-confirm"
      destructive={pending.force}
      busy={busy}
      title={
        pending.force
          ? t("onboarding.vertical.confirm.switchTitle", { name: pending.name })
          : t("onboarding.vertical.confirm.installTitle", { name: pending.name })
      }
      confirmLabel={
        busy
          ? t("onboarding.vertical.confirm.working")
          : pending.force
            ? t("onboarding.vertical.confirm.confirmSwitch")
            : t("onboarding.vertical.confirm.confirmInstall")
      }
      cancelLabel={t("onboarding.vertical.confirm.cancel")}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <p>
        {pending.force
          ? t("onboarding.vertical.confirm.switchBody", { current: currentName ?? "—" })
          : t("onboarding.vertical.confirm.installBody")}
      </p>
      {pending.force ? (
        <>
          <p>{t("onboarding.vertical.confirm.consequencesHeading")}</p>
          <ul>
            <li>{t("onboarding.vertical.confirm.consequenceSupersede")}</li>
            <li>{t("onboarding.vertical.confirm.consequenceAdopt")}</li>
            <li>{t("onboarding.vertical.confirm.consequenceOrphans")}</li>
            <li>{t("onboarding.vertical.confirm.consequenceAfterLive")}</li>
          </ul>
        </>
      ) : null}
    </WizardDialog>
  );
}

/** The install mutation's failure modes (§5). Two get their own recovery; the
 * rest fall through to the shared wizard notice (ALREADY_LIVE / RESOURCE_BUSY /
 * generic-with-request-id). */
function InstallProblem({
  error,
  onSwitch,
  onReloadPacks,
  onRetry,
}: {
  error: unknown;
  onSwitch: () => void;
  onReloadPacks: () => void;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  if (error === null || error === undefined) return null;

  if (isCode(error, "PACK_ALREADY_INSTALLED")) {
    return (
      <div className="state state-error wizard-notice" role="alert" data-testid="pack-already">
        <p className="state-title">{t("onboarding.vertical.problem.alreadyInstalledTitle")}</p>
        <p className="state-detail">{t("onboarding.vertical.problem.alreadyInstalledBody")}</p>
        <Button onClick={onSwitch}>{t("onboarding.vertical.current.switchCta")}</Button>
      </div>
    );
  }

  if (isCode(error, "PACK_UNKNOWN")) {
    return (
      <div className="state state-error wizard-notice" role="alert" data-testid="pack-unknown">
        <p className="state-title">{t("onboarding.vertical.problem.unknownTitle")}</p>
        <p className="state-detail">{t("onboarding.vertical.problem.unknownBody")}</p>
        <Button onClick={onReloadPacks}>{t("onboarding.vertical.problem.unknownReload")}</Button>
      </div>
    );
  }

  return (
    <WizardProblemNotice
      error={error}
      onRetry={onRetry}
      titleKey="onboarding.vertical.problem.failedTitle"
      detailKey="onboarding.vertical.problem.failedDetail"
    />
  );
}

// ---------------------------------------------------------------------------
// small helpers
// ---------------------------------------------------------------------------

type TFn = ReturnType<typeof useTranslation>["t"];

/**
 * English plural → singular for the pack kind keys.
 *
 * `counts_by_kind` is keyed by the PLURAL kind name (§2.3, e.g. "units",
 * "waste_categories"); the labels are keyed by the singular `object_kind`
 * (§2.4, "unit", "waste_category"). Chopping a trailing "s" turned
 * "waste_categories" into "waste_categorie", which matches nothing — so the
 * Cable & wire card advertised "9 waste_categories" beside "14 Waste causes"
 * and "13 Units of measure" (surface review F13).
 */
function singularKind(pluralKind: string): string {
  if (pluralKind.endsWith("ies")) return `${pluralKind.slice(0, -3)}y`;
  if (pluralKind.endsWith("ses") || pluralKind.endsWith("xes") || pluralKind.endsWith("ches")) {
    return pluralKind.slice(0, -2);
  }
  if (pluralKind.endsWith("s")) return pluralKind.slice(0, -1);
  return pluralKind;
}

/** `waste_category` → "Waste category". The last-resort label, so that a pack
 * kind this build has never heard of still reads as words rather than as a
 * database identifier. */
function humanizeKind(kind: string): string {
  const words = kind.replace(/_/gu, " ").trim();
  return words.length === 0 ? kind : words.charAt(0).toUpperCase() + words.slice(1);
}

function kindLabel(t: TFn, pluralKind: string): string {
  return t(`onboarding.vertical.kinds.${singularKind(pluralKind)}`, {
    defaultValue: t(`onboarding.vertical.kinds.${pluralKind}`, {
      defaultValue: humanizeKind(pluralKind),
    }),
  });
}

function packName(pack: PackSummary | undefined, locale: string, fallback: string): string {
  if (pack === undefined) return fallback;
  return localeText(pack.name, locale) ?? pack.key;
}

/** Read `previewed_packs[]` out of the step's untyped scratch payload (§2.2). */
function readPreviewed(payload: Record<string, unknown>): string[] {
  const value = payload["previewed_packs"];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

/**
 * Read `selected_pack_key` out of the step's untyped scratch payload.
 *
 * NOT the dirty baseline (it was, and that was surface-review blocker F1): the
 * scratch copy of the selected pack is a stale mirror of the install read, so
 * comparing it against the live one made an untouched step report unsaved work.
 * The baseline derives the member from `activePackKey(current)` on both sides.
 * Kept and exercised because the saved payload is still a real §2.2 record of
 * what step 1 last wrote, and reading it defensively is the shape every other
 * payload reader in this area has.
 */
export function readSelectedPackKey(payload: Record<string, unknown>): string | null {
  const value = payload["selected_pack_key"];
  return typeof value === "string" ? value : null;
}
